import type { SupervisorAuditSampleItem } from '@avisoma/shared';
import clsx from 'clsx';
import { useState } from 'react';
import { Button } from '../../catalyst/button';
import { TabEmptyState } from '../projectView/TabEmptyState';
import { AuditFindingPanel } from './AuditFindingPanel';
import { AuditLabelForm } from './AuditLabelForm';
import { AuditPageImage } from './AuditPageImage';
import {
  afterLabel,
  afterSkip,
  progressLabel,
  sessionView,
  START_PROGRESS,
  submitErrorOutcome,
  type AuditProgress,
  type SubmitErrorOutcome,
} from './auditPresentation';
import { useSubmitAuditLabel } from './useSubmitAuditLabel';

/** A failed submit's note, shown while the reviewer is at `at`: the failed item, or the next one after a 404. */
type SubmitNotice = { at: number; outcome: SubmitErrorOutcome };

/** One sample, one finding at a time. Key it by the sample round so a new sample starts at the first item. */
export function AuditSession({ items, onLoadMore }: { items: SupervisorAuditSampleItem[]; onLoadMore: () => void }) {
  const [progress, setProgress] = useState<AuditProgress>(START_PROGRESS);
  const [notice, setNotice] = useState<SubmitNotice | null>(null);
  const submit = useSubmitAuditLabel();
  const { position, labelled } = progress;
  const view = sessionView(position, items.length);
  const progressLine = progressLabel(labelled, items.length, position - labelled);
  const shown = notice?.at === position ? notice.outcome : null;
  const noticeLine = shown && (
    <p
      role="status"
      className={clsx('text-sm', shown.advance ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400')}
    >
      {shown.note}
    </p>
  );
  if (view === 'empty')
    return (
      <div className="space-y-4">
        <TabEmptyState message="No findings left for you to label." />
        <Button outline onClick={onLoadMore}>
          Check again
        </Button>
      </div>
    );
  if (view === 'done')
    return (
      <div className="space-y-4 text-center">
        {noticeLine}
        <p className="text-sm text-gray-600 dark:text-zinc-400">{progressLine}</p>
        <Button color="indigo" onClick={onLoadMore}>
          Load more
        </Button>
      </div>
    );
  const item = items[position];
  const onError = (error: unknown) => {
    const outcome = submitErrorOutcome(error);
    if (outcome.advance) setProgress((p) => afterSkip(p, position));
    setNotice({ at: outcome.advance ? position + 1 : position, outcome });
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-zinc-400">{progressLine}</p>
      {noticeLine}
      <div className="grid gap-6 lg:grid-cols-2">
        <AuditPageImage pageId={item.pageId} src={item.pageImageUrl} />
        <div className="space-y-4">
          <AuditFindingPanel item={item} />
          <AuditLabelForm
            key={item.finding.id}
            hasProposal={item.finding.hasProposal}
            busy={submit.isPending}
            onSubmit={(body) =>
              submit.mutate(
                { findingId: item.finding.id, body: { ...body, checkVersion: item.finding.checkVersion } },
                { onSuccess: () => setProgress((p) => afterLabel(p, position)), onError },
              )
            }
            onSkip={() => setProgress((p) => afterSkip(p, position))}
          />
        </div>
      </div>
    </div>
  );
}
